import { useState, useMemo } from "react";
import { Loader2, Sparkles, RefreshCw, Eye, Share2, TrendingUp, ArrowUpDown, ChevronLeft, ChevronRight } from "lucide-react";
import { Button } from "@/components/ui/button";
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from "@/components/ui/card";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from "@/components/ui/table";
import { useAdminWrapped } from "@/hooks/useAdminWrapped";
import { BarChart, Bar, XAxis, YAxis, Tooltip, ResponsiveContainer } from "recharts";

const SLIDE_LABELS: Record<string, string> = {
  "intro": "Intro",
  "total-events": "Total Events",
  "besties": "Besties",
  "top-host": "Top Host",
  "streak": "Streak",
  "galleries": "Strolls",
  "salons": "Salons",
  "summary": "Summary",
};

type SortField = "name" | "total_events" | "total_views" | "total_shares" | "last_viewed_at";
type SortDirection = "asc" | "desc";

const ITEMS_PER_PAGE = 10;

const WrappedAdmin = () => {
  const [selectedYear, setSelectedYear] = useState(2025);
  const [sortField, setSortField] = useState<SortField>("total_views");
  const [sortDirection, setSortDirection] = useState<SortDirection>("desc");
  const [currentPage, setCurrentPage] = useState(1);
  const { stats, isLoading, isGenerating, generateStats } = useAdminWrapped();

  const totalUsers = stats?.length || 0;
  const latestUpdate = stats?.[0]?.updated_at 
    ? new Date(stats[0].updated_at).toLocaleString()
    : "Never";

  const usersByYear = stats?.reduce((acc, stat) => {
    acc[stat.year] = (acc[stat.year] || 0) + 1;
    return acc;
  }, {} as Record<number, number>) || {};

  // Sort and paginate users
  const sortedUsers = useMemo(() => {
    if (!stats?.length) return [];

    const sorted = [...stats].sort((a, b) => {
      let aVal: string | number | null;
      let bVal: string | number | null;

      switch (sortField) {
        case "name":
          aVal = `${a.first_name || ""} ${a.last_name || ""}`.trim().toLowerCase();
          bVal = `${b.first_name || ""} ${b.last_name || ""}`.trim().toLowerCase();
          break;
        case "total_events":
          aVal = a.total_events || 0;
          bVal = b.total_events || 0;
          break;
        case "total_views":
          aVal = a.total_views || 0;
          bVal = b.total_views || 0;
          break;
        case "total_shares":
          aVal = a.total_shares || 0;
          bVal = b.total_shares || 0;
          break;
        case "last_viewed_at":
          aVal = a.last_viewed_at ? new Date(a.last_viewed_at).getTime() : 0;
          bVal = b.last_viewed_at ? new Date(b.last_viewed_at).getTime() : 0;
          break;
        default:
          return 0;
      }

      if (aVal === bVal) return 0;
      if (sortDirection === "asc") {
        return aVal < bVal ? -1 : 1;
      }
      return aVal > bVal ? -1 : 1;
    });

    return sorted;
  }, [stats, sortField, sortDirection]);

  const totalPages = Math.ceil(sortedUsers.length / ITEMS_PER_PAGE);
  const paginatedUsers = sortedUsers.slice(
    (currentPage - 1) * ITEMS_PER_PAGE,
    currentPage * ITEMS_PER_PAGE
  );

  const handleSort = (field: SortField) => {
    if (sortField === field) {
      setSortDirection(prev => prev === "asc" ? "desc" : "asc");
    } else {
      setSortField(field);
      setSortDirection("desc");
    }
    setCurrentPage(1);
  };

  // Calculate engagement metrics
  const engagementMetrics = useMemo(() => {
    if (!stats?.length) return null;

    let totalViews = 0;
    let totalShares = 0;
    let usersWhoViewed = 0;
    let usersWhoShared = 0;
    const aggregateSlideViews: Record<string, number> = {};
    const aggregateShareClicks: Record<string, number> = {};

    stats.forEach((stat) => {
      const views = stat.total_views || 0;
      const shares = stat.total_shares || 0;
      
      totalViews += views;
      totalShares += shares;
      
      if (views > 0) usersWhoViewed++;
      if (shares > 0) usersWhoShared++;

      // Aggregate slide views
      const slideViews = (stat.slide_views as Record<string, number>) || {};
      for (const [slide, count] of Object.entries(slideViews)) {
        aggregateSlideViews[slide] = (aggregateSlideViews[slide] || 0) + count;
      }

      // Aggregate share clicks
      const shareClicks = (stat.share_clicks as Record<string, number>) || {};
      for (const [slide, count] of Object.entries(shareClicks)) {
        aggregateShareClicks[slide] = (aggregateShareClicks[slide] || 0) + count;
      }
    });

    // Convert to chart data
    const slideViewsData = Object.entries(aggregateSlideViews)
      .map(([slide, count]) => ({
        slide: SLIDE_LABELS[slide] || slide,
        views: count,
      }))
      .sort((a, b) => b.views - a.views);

    const shareClicksData = Object.entries(aggregateShareClicks)
      .map(([slide, count]) => ({
        slide: SLIDE_LABELS[slide] || slide,
        shares: count,
      }))
      .sort((a, b) => b.shares - a.shares);

    return {
      totalViews,
      totalShares,
      usersWhoViewed,
      usersWhoShared,
      avgViewsPerUser: usersWhoViewed > 0 ? (totalViews / usersWhoViewed).toFixed(1) : "0",
      slideViewsData,
      shareClicksData,
      mostViewedSlide: slideViewsData[0]?.slide || "N/A",
      mostSharedSlide: shareClicksData[0]?.slide || "N/A",
    };
  }, [stats]);

  const SortableHeader = ({ field, children }: { field: SortField; children: React.ReactNode }) => (
    <TableHead 
      className="cursor-pointer hover:bg-muted/50 transition-colors"
      onClick={() => handleSort(field)}
    >
      <div className="flex items-center gap-1">
        {children}
        <ArrowUpDown className={`h-3 w-3 ${sortField === field ? "text-secondary" : "text-muted-foreground"}`} />
      </div>
    </TableHead>
  );

  return (
    <div className="space-y-6">
      <Card>
        <CardHeader>
          <CardTitle className="flex items-center gap-2">
            <Sparkles className="h-5 w-5 text-secondary" />
            Generate Wrapped Stats
          </CardTitle>
          <CardDescription>
            Compute Year in Art statistics for all users with confirmed tickets
          </CardDescription>
        </CardHeader>
        <CardContent className="space-y-4">
          <div className="flex items-center gap-4">
            <Select
              value={String(selectedYear)}
              onValueChange={(v) => setSelectedYear(Number(v))}
            >
              <SelectTrigger className="w-32">
                <SelectValue />
              </SelectTrigger>
              <SelectContent>
                <SelectItem value="2024">2024</SelectItem>
                <SelectItem value="2025">2025</SelectItem>
              </SelectContent>
            </Select>

            <Button
              onClick={() => generateStats(selectedYear)}
              disabled={isGenerating}
              variant="secondary"
            >
              {isGenerating ? (
                <>
                  <Loader2 className="h-4 w-4 animate-spin mr-2" />
                  Generating...
                </>
              ) : (
                <>
                  <RefreshCw className="h-4 w-4 mr-2" />
                  Generate Stats
                </>
              )}
            </Button>
          </div>

          <p className="text-sm text-muted-foreground">
            This will calculate and store wrapped statistics for all users who attended events in {selectedYear}.
            Existing stats for {selectedYear} will be updated.
          </p>
        </CardContent>
      </Card>

      <Card>
        <CardHeader>
          <CardTitle>Stats Overview</CardTitle>
          <CardDescription>Current wrapped data in database</CardDescription>
        </CardHeader>
        <CardContent>
          {isLoading ? (
            <div className="flex items-center justify-center py-8">
              <Loader2 className="h-6 w-6 animate-spin text-muted-foreground" />
            </div>
          ) : (
            <div className="grid grid-cols-2 md:grid-cols-4 gap-4">
              <div className="p-4 bg-muted/50 rounded-lg">
                <p className="text-2xl font-bold text-foreground">{totalUsers}</p>
                <p className="text-sm text-muted-foreground">Total Users</p>
              </div>
              <div className="p-4 bg-muted/50 rounded-lg">
                <p className="text-2xl font-bold text-foreground">{usersByYear[2024] || 0}</p>
                <p className="text-sm text-muted-foreground">2024 Users</p>
              </div>
              <div className="p-4 bg-muted/50 rounded-lg">
                <p className="text-2xl font-bold text-foreground">{usersByYear[2025] || 0}</p>
                <p className="text-sm text-muted-foreground">2025 Users</p>
              </div>
              <div className="p-4 bg-muted/50 rounded-lg">
                <p className="text-sm font-medium text-foreground">{latestUpdate}</p>
                <p className="text-sm text-muted-foreground">Last Update</p>
              </div>
            </div>
          )}
        </CardContent>
      </Card>

      {/* Engagement Stats Card */}
      <Card>
        <CardHeader>
          <CardTitle className="flex items-center gap-2">
            <TrendingUp className="h-5 w-5 text-secondary" />
            Engagement Analytics
          </CardTitle>
          <CardDescription>How users are interacting with Wrapped</CardDescription>
        </CardHeader>
        <CardContent>
          {isLoading ? (
            <div className="flex items-center justify-center py-8">
              <Loader2 className="h-6 w-6 animate-spin text-muted-foreground" />
            </div>
          ) : engagementMetrics ? (
            <div className="space-y-6">
              {/* Summary Stats */}
              <div className="grid grid-cols-2 md:grid-cols-4 gap-4">
                <div className="p-4 bg-muted/50 rounded-lg">
                  <div className="flex items-center gap-2 mb-1">
                    <Eye className="h-4 w-4 text-secondary" />
                    <p className="text-2xl font-bold text-foreground">{engagementMetrics.totalViews}</p>
                  </div>
                  <p className="text-sm text-muted-foreground">Total Views</p>
                </div>
                <div className="p-4 bg-muted/50 rounded-lg">
                  <div className="flex items-center gap-2 mb-1">
                    <Share2 className="h-4 w-4 text-secondary" />
                    <p className="text-2xl font-bold text-foreground">{engagementMetrics.totalShares}</p>
                  </div>
                  <p className="text-sm text-muted-foreground">Total Shares</p>
                </div>
                <div className="p-4 bg-muted/50 rounded-lg">
                  <p className="text-2xl font-bold text-foreground">{engagementMetrics.usersWhoViewed}</p>
                  <p className="text-sm text-muted-foreground">Users Viewed</p>
                </div>
                <div className="p-4 bg-muted/50 rounded-lg">
                  <p className="text-2xl font-bold text-foreground">{engagementMetrics.usersWhoShared}</p>
                  <p className="text-sm text-muted-foreground">Users Shared</p>
                </div>
              </div>

              {/* Additional Metrics */}
              <div className="grid grid-cols-2 md:grid-cols-3 gap-4">
                <div className="p-4 bg-muted/50 rounded-lg">
                  <p className="text-lg font-bold text-foreground">{engagementMetrics.avgViewsPerUser}</p>
                  <p className="text-sm text-muted-foreground">Avg Views/User</p>
                </div>
                <div className="p-4 bg-muted/50 rounded-lg">
                  <p className="text-lg font-bold text-foreground">{engagementMetrics.mostViewedSlide}</p>
                  <p className="text-sm text-muted-foreground">Most Viewed Slide</p>
                </div>
                <div className="p-4 bg-muted/50 rounded-lg">
                  <p className="text-lg font-bold text-foreground">{engagementMetrics.mostSharedSlide}</p>
                  <p className="text-sm text-muted-foreground">Most Shared Slide</p>
                </div>
              </div>

              {/* Charts */}
              {engagementMetrics.slideViewsData.length > 0 && (
                <div className="space-y-4">
                  <h4 className="text-sm font-medium text-foreground">Views by Slide</h4>
                  <div className="h-48">
                    <ResponsiveContainer width="100%" height="100%">
                      <BarChart data={engagementMetrics.slideViewsData}>
                        <XAxis 
                          dataKey="slide" 
                          tick={{ fill: 'hsl(var(--muted-foreground))', fontSize: 12 }}
                          angle={-45}
                          textAnchor="end"
                          height={60}
                        />
                        <YAxis tick={{ fill: 'hsl(var(--muted-foreground))', fontSize: 12 }} />
                        <Tooltip 
                          contentStyle={{ 
                            backgroundColor: 'hsl(var(--card))', 
                            border: '1px solid hsl(var(--border))',
                            borderRadius: '8px'
                          }}
                        />
                        <Bar dataKey="views" fill="hsl(var(--secondary))" radius={[4, 4, 0, 0]} />
                      </BarChart>
                    </ResponsiveContainer>
                  </div>
                </div>
              )}

              {engagementMetrics.shareClicksData.length > 0 && (
                <div className="space-y-4">
                  <h4 className="text-sm font-medium text-foreground">Shares by Slide</h4>
                  <div className="h-48">
                    <ResponsiveContainer width="100%" height="100%">
                      <BarChart data={engagementMetrics.shareClicksData}>
                        <XAxis 
                          dataKey="slide" 
                          tick={{ fill: 'hsl(var(--muted-foreground))', fontSize: 12 }}
                          angle={-45}
                          textAnchor="end"
                          height={60}
                        />
                        <YAxis tick={{ fill: 'hsl(var(--muted-foreground))', fontSize: 12 }} />
                        <Tooltip 
                          contentStyle={{ 
                            backgroundColor: 'hsl(var(--card))', 
                            border: '1px solid hsl(var(--border))',
                            borderRadius: '8px'
                          }}
                        />
                        <Bar dataKey="shares" fill="hsl(var(--primary))" radius={[4, 4, 0, 0]} />
                      </BarChart>
                    </ResponsiveContainer>
                  </div>
                </div>
              )}
            </div>
          ) : (
            <p className="text-muted-foreground text-center py-4">No engagement data yet</p>
          )}
        </CardContent>
      </Card>

      {/* User Analytics Table */}
      <Card>
        <CardHeader>
          <CardTitle>User Analytics</CardTitle>
          <CardDescription>Individual user engagement with Wrapped (click headers to sort)</CardDescription>
        </CardHeader>
        <CardContent>
          {isLoading ? (
            <div className="flex items-center justify-center py-8">
              <Loader2 className="h-6 w-6 animate-spin text-muted-foreground" />
            </div>
          ) : sortedUsers.length > 0 ? (
            <div className="space-y-4">
              <div className="rounded-md border overflow-x-auto">
                <Table>
                  <TableHeader>
                    <TableRow>
                      <SortableHeader field="name">Name</SortableHeader>
                      <TableHead>Email</TableHead>
                      <SortableHeader field="total_events">Events</SortableHeader>
                      <SortableHeader field="total_views">Views</SortableHeader>
                      <SortableHeader field="total_shares">Shares</SortableHeader>
                      <TableHead>Shared Slides</TableHead>
                      <SortableHeader field="last_viewed_at">Last Viewed</SortableHeader>
                    </TableRow>
                  </TableHeader>
                  <TableBody>
                    {paginatedUsers.map((user) => {
                      const sharedSlides = Object.entries((user.share_clicks as Record<string, number>) || {})
                        .filter(([, count]) => count > 0)
                        .map(([slide]) => SLIDE_LABELS[slide] || slide);
                      
                      return (
                      <TableRow key={user.id} className="h-9">
                        <TableCell className="font-medium py-1">
                          {user.first_name || user.last_name 
                            ? `${user.first_name || ""} ${user.last_name || ""}`.trim()
                            : "—"}
                        </TableCell>
                        <TableCell className="text-muted-foreground text-sm py-1">
                          {user.email || "—"}
                        </TableCell>
                        <TableCell className="py-1">{user.total_events || 0}</TableCell>
                        <TableCell className="py-1">
                          <div className="flex items-center gap-1">
                            <Eye className="h-3 w-3 text-muted-foreground" />
                            {user.total_views || 0}
                          </div>
                        </TableCell>
                        <TableCell className="py-1">
                          <div className="flex items-center gap-1">
                            <Share2 className="h-3 w-3 text-muted-foreground" />
                            {user.total_shares || 0}
                          </div>
                        </TableCell>
                        <TableCell className="py-1">
                          {sharedSlides.length > 0 ? (
                            <div className="flex flex-wrap gap-1">
                              {sharedSlides.map((slide) => (
                                <span 
                                  key={slide}
                                  className="text-xs bg-secondary/20 text-secondary px-1.5 py-0.5 rounded"
                                >
                                  {slide}
                                </span>
                              ))}
                            </div>
                          ) : (
                            <span className="text-muted-foreground">—</span>
                          )}
                        </TableCell>
                        <TableCell className="text-muted-foreground text-sm py-1">
                          {user.last_viewed_at 
                            ? new Date(user.last_viewed_at).toLocaleString()
                            : "Never"}
                        </TableCell>
                      </TableRow>
                      );
                    })}
                  </TableBody>
                </Table>
              </div>

              {/* Pagination */}
              {totalPages > 1 && (
                <div className="flex items-center justify-between">
                  <p className="text-sm text-muted-foreground">
                    Showing {(currentPage - 1) * ITEMS_PER_PAGE + 1} to {Math.min(currentPage * ITEMS_PER_PAGE, sortedUsers.length)} of {sortedUsers.length} users
                  </p>
                  <div className="flex items-center gap-2">
                    <Button
                      variant="outline"
                      size="sm"
                      onClick={() => setCurrentPage(prev => Math.max(1, prev - 1))}
                      disabled={currentPage === 1}
                    >
                      <ChevronLeft className="h-4 w-4" />
                    </Button>
                    <span className="text-sm text-muted-foreground">
                      Page {currentPage} of {totalPages}
                    </span>
                    <Button
                      variant="outline"
                      size="sm"
                      onClick={() => setCurrentPage(prev => Math.min(totalPages, prev + 1))}
                      disabled={currentPage === totalPages}
                    >
                      <ChevronRight className="h-4 w-4" />
                    </Button>
                  </div>
                </div>
              )}
            </div>
          ) : (
            <p className="text-muted-foreground text-center py-4">No users found</p>
          )}
        </CardContent>
      </Card>
    </div>
  );
};

export default WrappedAdmin;
